'use client';

import React, { useCallback, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useMediaQuery } from 'react-responsive';
import { WRAPPER_CLASS_NAMES } from '@/constants/ads.constants';
import IMAGE_SIZE from '@/constants/image-size.constants';
import OnDemandContent from '@/components/OnDemand/OnDemand';
import { ProfessionalCTA } from '@/components/CTA/professionalCTA';
import { useUser } from '@/utils/supabase/useUser';
import { useSetAtom } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { cartAtom } from '@/state';
import { FreeAccountCTA } from '@/components/CTA/freeAccountCTA';
import { AdSlot } from '@/components/Ads/GPT';
const ReadyMediaGroupAd = dynamic(
  () => import('@/components/Ads/ReadyMediaGroupAd')
);

type OnDemandDetailsProps = {
  onDemand: OnDemand;
  hasAccessGranted: boolean;
  // showInArticleAds: boolean;
};

const MAX_CHECK_COUNT = 100;
const REFRESH_INTERVAL = 5000;

const OnDemandDetailsView = ({
  onDemand,
  hasAccessGranted
}: OnDemandDetailsProps) => {
  const resetCart = useSetAtom(cartAtom);
  const [viewAll, setViewAll] = useState(hasAccessGranted);
  const [content, setContent] = useState<OnDemand>(onDemand);
  const { user } = useUser();
  const searchParams = useSearchParams();
  const checkPurchaseStatus = searchParams.get('redirect');
  const [intervalId, setIntervalId] = useState<number | null>(null);
  const [checkCount, setCheckCount] = useState(0);

  // Show content if price is 0 and user is logged in
  useEffect(() => {
    if (onDemand.price === 0 && user) {
      setViewAll(true);
    }
  }, [onDemand.price, user]);

  const refreshPurchaseStatus = useCallback(
    async (id: string) => {
      const response = await fetch(`/api/on-demand/${id}`);
      const data = (await response.json()) as {
        hasAccessGranted: boolean;
        content: OnDemand;
      };
      if (data.hasAccessGranted) {
        if (intervalId) {
          clearTimeout(intervalId);
        }
        setViewAll(true);
        setContent(data.content);
        resetCart([]);
      }
      // return data;
    },
    [intervalId, resetCart]
  );

  useEffect(() => {
    if (checkPurchaseStatus === '1') {
      void refreshPurchaseStatus(onDemand.id);
      const interval = window.setTimeout(() => {
        if (checkCount < MAX_CHECK_COUNT && !viewAll) {
          void refreshPurchaseStatus(onDemand.id);
          setCheckCount((prevState) => prevState + 1);
        } else {
          if (intervalId) {
            clearTimeout(intervalId);
          }
        }
      }, REFRESH_INTERVAL);
      setIntervalId(interval);
    }
    return () => {
      if (intervalId) {
        clearTimeout(intervalId);
      }
    };
  }, [
    checkCount,
    checkPurchaseStatus,
    intervalId,
    onDemand.id,
    refreshPurchaseStatus,
    viewAll
  ]);

  const isMobile = useMediaQuery({ query: '(max-width: 760px)' });
  const isTablet = useMediaQuery({ query: '(max-width: 1279px)' }) && !isMobile;

  return (
    <div className='article-container'>
      <div className='article-container__inner'>
        <div className='article__main ondemand'>
          <OnDemandContent
            content={content}
            imageSize={IMAGE_SIZE.ARTICLE_FEATURE}
            canViewRecordings={viewAll || (content.price === 0 && !!user)}
          />
          {!viewAll ? (
            onDemand.price > 0 ? (
              <ProfessionalCTA
                isLoggedIn={user ? true : false}
                product={{
                  id: onDemand.id,
                  name: onDemand.title,
                  price: onDemand.price,
                  quantity: 0,
                  description: onDemand.summary
                }}
              />
            ) : (
              <FreeAccountCTA isLoggedIn={user ? true : false} />
            )
          ) : null}
        </div>
        {!isMobile && (
          <div className='article__widget-container'>
            <div className='article__widget-inner'>
              <AdSlot
                id='article_rhs'
                wrapperClassName={WRAPPER_CLASS_NAMES.HALF_PAGE_ADS}
              />
              <ReadyMediaGroupAd />
            </div>
          </div>
        )}
      </div>

      {!!isMobile && (
        <div className='article__widget-inner article__main'>
          <div style={{ margin: '5px 0' }}>
            <AdSlot
              id='article_rhs'
              wrapperClassName={WRAPPER_CLASS_NAMES.HALF_PAGE_ADS}
            />
          </div>
          <div style={{ margin: '5px 0' }}>
            <ReadyMediaGroupAd />
          </div>
        </div>
      )}
      {!!isTablet && (
        <div className='article__widget-inner article__main'>
          <div style={{ margin: '5px 0' }}>
            <ReadyMediaGroupAd />
          </div>
        </div>
      )}
    </div>
  );
};

export default OnDemandDetailsView;
